import {
  array,
  boolean,
  check,
  finite,
  type InferOutput,
  literal,
  looseObject,
  maxValue,
  minValue,
  nullable,
  number,
  object,
  optional,
  parser,
  picklist,
  pipe,
  regex,
  safeParse,
  string,
  transform,
  unknown,
  variant,
} from "valibot";

import {
  vPaymentWidgetPaymentResultResponseDto,
  vPaymentWidgetQuoteResponseDto,
  vPublicPaymentWidgetResponseDto,
} from "./generated/public-api/valibot.gen.ts";
import {
  customFieldsSchema,
  nonEmptyStringSchema,
  optionalNullableStringSchema,
  safeIntegerSchema,
} from "./schema-helpers.ts";

const paymentOptionSchema = picklist([
  "one-time",
  "monthly",
  "annually",
  "every-six-months",
]);
const fulfillmentTypeSchema = picklist(["none", "event_admission"]);
const nullableNonNegativeIntegerSchema = nullable(safeIntegerSchema(0));
const availabilityStatusSchema = picklist(["available", "sold_out"]);
const availabilitySchema = optional(
  object({
    listings: array(
      object({
        listingId: nonEmptyStringSchema,
        maxPurchasableQuantity: nullableNonNegativeIntegerSchema,
        remainingQuantity: nullableNonNegativeIntegerSchema,
        status: availabilityStatusSchema,
      })
    ),
    sharedLimits: array(
      object({
        id: nonEmptyStringSchema,
        listingIds: array(nonEmptyStringSchema),
        remainingQuantity: safeIntegerSchema(0),
        status: availabilityStatusSchema,
      })
    ),
  }),
  { listings: [], sharedLimits: [] }
);
const publishableKeySchema = pipe(
  string(),
  regex(/^pk_(?:test|live)_\S+$/, "Invalid publishable key.")
);

const productBaseEntries = {
  description: optionalNullableStringSchema,
  fulfillmentType: fulfillmentTypeSchema,
  id: nonEmptyStringSchema,
  inventoryQuantity: nullableNonNegativeIntegerSchema,
  maxQuantity: nullableNonNegativeIntegerSchema,
  minimumAmountCents: nullableNonNegativeIntegerSchema,
  minQuantity: nullableNonNegativeIntegerSchema,
  name: nonEmptyStringSchema,
  paymentOption: paymentOptionSchema,
};

const eventSchema = object({
  description: nullable(string()),
  endAt: nonEmptyStringSchema,
  id: nonEmptyStringSchema,
  location: nullable(string()),
  name: nonEmptyStringSchema,
  startAt: nonEmptyStringSchema,
  status: picklist(["draft", "active", "finished", "cancelled"]),
});

const publicProductSchema = variant("pricingMode", [
  object({
    ...productBaseEntries,
    amountCents: safeIntegerSchema(0),
    pricingMode: literal("fixed"),
  }),
  object({
    ...productBaseEntries,
    amountCents: nullableNonNegativeIntegerSchema,
    pricingMode: literal("custom"),
  }),
]);

const productListingSchema = object({
  acceptsDiscounts: optional(boolean(), false),
  id: nonEmptyStringSchema,
  maxQuantityPerOrder: optional(nullableNonNegativeIntegerSchema, null),
  product: publicProductSchema,
  productId: nonEmptyStringSchema,
  slug: nonEmptyStringSchema,
});

const widgetFieldsSchema = optional(
  object({
    memberId: optional(boolean(), false),
    merchSize: optional(boolean(), false),
    phone: optional(boolean(), false),
    promoCode: optional(boolean(), false),
    serviceFee: optional(boolean(), false),
    shipping: optional(boolean(), false),
    tipping: optional(boolean(), false),
  }),
  {}
);

const requiredShippingSchema = optional(
  object({
    additionalUnitAmountCents: optional(
      pipe(safeIntegerSchema(0), maxValue(2_147_483_647))
    ),
    productId: nonEmptyStringSchema,
    triggerProductIds: array(nonEmptyStringSchema),
  })
);

export const publicWidgetSchema = pipe(
  object({
    availability: availabilitySchema,
    community: optional(
      object({
        color: string(),
        memberIdLabel: optional(nullable(string())),
        name: nonEmptyStringSchema,
        thumbnail: nullable(string()),
        widgetAchEnabled: optional(boolean()),
      })
    ),
    config: optional(
      object({
        customFields: customFieldsSchema,
        fields: widgetFieldsSchema,
        requiredShipping: requiredShippingSchema,
      })
    ),
    description: optionalNullableStringSchema,
    event: nullable(eventSchema),
    id: optional(string(), ""),
    name: optional(string(), "Lockerverse widget"),
    payment: object({
      connectedAccountId: optional(nullable(nonEmptyStringSchema), null),
      currency: literal("usd"),
      publishableKey: publishableKeySchema,
    }),
    pricingPolicy: optional(
      object({
        currency: optional(literal("usd"), "usd"),
        serviceFeeRateBasisPoints: optional(safeIntegerSchema(0), 0),
      })
    ),
    products: optional(array(productListingSchema), []),
    showCommunityBranding: optional(boolean(), true),
    title: optionalNullableStringSchema,
  }),
  transform((widget) => ({
    ...widget,
    config: {
      customFields: widget.config?.customFields ?? [],
      fields: {
        memberId: widget.config?.fields?.memberId ?? false,
        merchSize: widget.config?.fields?.merchSize ?? false,
        phone: widget.config?.fields?.phone ?? false,
        promoCode: widget.config?.fields?.promoCode ?? false,
        serviceFee: widget.config?.fields?.serviceFee ?? false,
        shipping: widget.config?.fields?.shipping ?? false,
        tipping: widget.config?.fields?.tipping ?? false,
      },
      requiredShipping: widget.config?.requiredShipping,
    },
    pricingPolicy: widget.pricingPolicy ?? {
      currency: "usd" as const,
      serviceFeeRateBasisPoints: 0,
    },
  }))
);

const catalogProductSchema = object({
  acceptsDiscounts: boolean(),
  amountCents: nullable(safeIntegerSchema(0)),
  description: nullable(string()),
  fulfillmentType: fulfillmentTypeSchema,
  id: nonEmptyStringSchema,
  inventoryQuantity: nullableNonNegativeIntegerSchema,
  listingId: nonEmptyStringSchema,
  maximumQuantity: nullableNonNegativeIntegerSchema,
  minimumAmountCents: nullableNonNegativeIntegerSchema,
  minimumQuantity: nullableNonNegativeIntegerSchema,
  name: nonEmptyStringSchema,
  paymentOption: paymentOptionSchema,
  pricingMode: picklist(["fixed", "custom"]),
  slug: nonEmptyStringSchema,
});

const catalogSchema = object({
  availability: availabilitySchema,
  community: nullable(
    object({
      accentColor: string(),
      imageUrl: nullable(string()),
      memberIdLabel: optional(nullable(string())),
      name: nonEmptyStringSchema,
      widgetAchEnabled: optional(boolean()),
    })
  ),
  event: nullable(eventSchema),
  payment: object({
    connectedAccountId: optional(nullable(nonEmptyStringSchema), null),
    currency: literal("usd"),
    publishableKey: publishableKeySchema,
  }),
  pricingPolicy: object({
    currency: literal("usd"),
    serviceFeeRateBasisPoints: safeIntegerSchema(0),
  }),
  products: array(catalogProductSchema),
  widget: object({
    customFields: customFieldsSchema,
    description: nullable(string()),
    fields: widgetFieldsSchema,
    id: string(),
    name: nonEmptyStringSchema,
    requiredShipping: requiredShippingSchema,
    showCommunityBranding: boolean(),
    title: nullable(string()),
  }),
});

const appliedDiscountSchema = nullable(
  object({
    amountOffCents: optional(safeIntegerSchema(0)),
    message: optional(string()),
    percentageOff: optional(
      pipe(number(), finite(), minValue(0), maxValue(100))
    ),
    source: picklist(["email_list", "promo_code"]),
  })
);

const checkoutLineItemSchema = object({
  acceptsDiscounts: boolean(),
  eventId: nullable(nonEmptyStringSchema),
  fulfillmentType: fulfillmentTypeSchema,
  listingId: nonEmptyStringSchema,
  listingSlug: nonEmptyStringSchema,
  paymentOption: paymentOptionSchema,
  pricingMode: picklist(["fixed", "custom"]),
  productId: nonEmptyStringSchema,
  productName: nonEmptyStringSchema,
  quantity: safeIntegerSchema(1),
  subtotalCents: safeIntegerSchema(0),
  unitAmountCents: safeIntegerSchema(0),
});

export const checkoutQuoteSchema = pipe(
  vPaymentWidgetQuoteResponseDto,
  object({
    amountCents: safeIntegerSchema(0),
    appliedDiscount: appliedDiscountSchema,
    connectedAccountId: nullable(nonEmptyStringSchema),
    discountableSubtotalCents: safeIntegerSchema(0),
    discountCents: safeIntegerSchema(0),
    lineItems: array(checkoutLineItemSchema),
    paymentOption: paymentOptionSchema,
    serviceFeeCents: safeIntegerSchema(0),
    subtotalCents: safeIntegerSchema(0),
    tipCents: safeIntegerSchema(0),
    totalCents: safeIntegerSchema(0),
  }),
  check(({ lineItems, paymentOption }) => {
    if (
      lineItems.length === 0 ||
      lineItems.some((item) => item.paymentOption !== paymentOption)
    ) {
      return false;
    }
    return (
      paymentOption === "one-time" ||
      (lineItems.length === 1 &&
        lineItems[0]?.pricingMode === "fixed" &&
        lineItems[0].quantity === 1)
    );
  }, "Invalid checkout cadence composition.")
);

export const paymentResultSchema = pipe(
  object({
    ...vPaymentWidgetPaymentResultResponseDto.entries,
    retryUrl: optional(pipe(nonEmptyStringSchema, regex(/^https:\/\//))),
  }),
  check((payment) => {
    if (!(payment.checkoutId.trim() && payment.paymentReference.trim())) {
      return false;
    }
    if (payment.checkoutStatus === "action_required") {
      return (
        payment.paymentRequired &&
        ((payment.requiresAction && Boolean(payment.clientSecret?.trim())) ||
          (!payment.requiresAction &&
            payment.clientSecret === null &&
            Boolean(payment.retryUrl)))
      );
    }
    return !payment.requiresAction && payment.clientSecret === null;
  }, "Invalid payment result state.")
);

export type PublicCheckoutQuote = InferOutput<typeof checkoutQuoteSchema>;
export type PublicProductListing = InferOutput<typeof productListingSchema>;
export type PublicWidget = InferOutput<typeof publicWidgetSchema>;

export const parseCheckoutQuote = parser(checkoutQuoteSchema);
export const parseCatalog = parser(catalogSchema);
export const parsePaymentResult = parser(paymentResultSchema);
const parsePublicWidgetContract = parser(vPublicPaymentWidgetResponseDto);
const parseSdkPublicWidget = parser(publicWidgetSchema);

export function parsePublicWidget<Input>(input: Input) {
  parsePublicWidgetContract(input);
  return parseSdkPublicWidget(input);
}

export function parsePublicWidgetLoad<Input>(input: Input) {
  const response = safeParse(
    looseObject({ quote: optional(unknown()) }),
    input
  );
  const widget = parsePublicWidget(input);
  const quote = response.success ? response.output.quote : undefined;
  return quote === undefined
    ? { widget }
    : { quote: parseCheckoutQuote(quote), widget };
}
